import { Component, computed, forwardRef, input, model, OnInit, signal, viewChild, OnDestroy, output, effect, ChangeDetectionStrategy } from '@angular/core';
import { LayoutColumnComponent } from 'src/app/controls/components/basic/layout-column/layout-column.component';
import { LayoutRowComponent } from 'src/app/controls/components/basic/layout-row/layout-row.component';
import { Format, FormCustomDocumentType, FormDocumentType, PossibleDocumentType, SelectedDocumentType } from './models/document-type';
import { LinkComponent } from 'src/app/controls/components/basic/link/link.component';
import { ControlValueAccessor, FormGroup, FormsModule, NG_VALIDATORS, NG_VALUE_ACCESSOR, NgForm, Validator } from '@angular/forms';
import { TextboxComponent } from 'src/app/controls/components/basic/textbox/textbox.component';
import { SingleselectboxComponent } from 'src/app/controls/components/basic/singleselectbox/singleselectbox.component';
import { CheckboxComponent } from 'src/app/controls/components/basic/checkbox/checkbox.component';

import { Subscription } from 'rxjs';
import { DocumentDirection, FormPartnerConversation, ItemStatus, NewCommentEvent, PartnerOnboardingComment, ToggleConversationEvent, Transport, TransportType } from '../../models/partner-onboarding';
import { OnboardingCommentsContainerComponent } from '../onboarding-comments-container/onboarding-comments-container.component';
import { OnboardingCommentsIconComponent } from '../onboarding-comments-icon/onboarding-comments-icon.component';
import { ConversationComponent } from 'src/app/controls/components/advanced/conversation/conversation.component';
import { Conversation, ConversationMessage } from 'src/app/controls/components/advanced/conversation/models/conversation';
import { OnboardingTransportComponent } from '../onboarding-transport/onboarding-transport.component';

@Component({
  selector: 'app-onboarding-documents',
  standalone: true,
  imports: [FormsModule, LayoutRowComponent, LayoutColumnComponent, TextboxComponent, SingleselectboxComponent, CheckboxComponent, LinkComponent, ConversationComponent, OnboardingCommentsContainerComponent, OnboardingCommentsIconComponent, OnboardingTransportComponent],
  templateUrl: './onboarding-documents.component.html',
  styleUrl: './onboarding-documents.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OnboardingDocumentsComponent),
      multi: true
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => OnboardingDocumentsComponent),
      multi: true
    }
  ],
})
export class OnboardingDocumentsComponent implements ControlValueAccessor, Validator, OnInit, OnDestroy {
  form = viewChild<NgForm>('form');
  possibleDocumentTypes = input.required<PossibleDocumentType[]>();
  possibleTransportTypes = input.required<Transport[]>();
  documentDirection = input.required<DocumentDirection>();
  model = model<SelectedDocumentType[]>([]);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  formStatusChanged = output<string>(); // emits: 'VALID' | 'INVALID' | 'PENDING' | 'DISABLED'
  requestCredentialsClicked = output<TransportType>();
  showCredentialsClicked = output<TransportType>();
  formChangesSubscription: Subscription;

  //Conversation related inputs and outputs
  allowConversation = input<boolean>(true);
  readonlyConversation = input<boolean>(false);
  conversation = input<FormPartnerConversation | null>(null);
  unreadConversationIds = input<string[]>([]);
  autoRefreshEnabled = input<boolean>(false);
  toggleAutoRefreshClicked = output<void>();
  toggleConversationClicked = output<ToggleConversationEvent>();
  newCommentClicked = output<NewCommentEvent>();

  constructor() {
    effect((onCleanup) => {
        const frm = this.form()?.form;
        if (!frm) return;
    
        // emit current status once and on subsequent changes
        this.formStatusChanged.emit(frm.status);
        const sub = frm.statusChanges.subscribe(status => this.formStatusChanged.emit(status));

        onCleanup(() => sub.unsubscribe());
    });
  }

  ngOnInit(): void {
    this.formChangesSubscription = this.form().form.valueChanges.subscribe(x => {
      this.onChanged(this.getSelectedDocumentTypes(x as FormGroup));
    })
  }

  // Computed signals for form fields

  formDocumentTypes = computed(() => this.possibleDocumentTypes()?.map(i => {
    const format = this.model()?.find(x => x.Id === i.Id)?.Format;
    const possibleFormats =  [...i.Formats];
    possibleFormats.push({Id: -1, Name: 'Other'});
    if (format) {
      format.Id = format.Id ?? -1;
    }
    return new FormDocumentType({
      Id: i.Id, 
      Name: i.Name, 
      PossibleFormats: possibleFormats, 
      SelectedFormat: format, 
      Selected: !!format, 
      CustomFormatName: format?.Id === -1 ? format.Name : null, 
      Transport: this.model()?.find(x => x.Id === i.Id)?.Transport ?? null,
      Status: this.model()?.find(x => x.Id === i.Id)?.Status ?? {Status: ItemStatus.Open, CreatedEntities: []},
      ConversationId: this.model()?.find(x => x.Id === i.Id)?.ConversationId ?? null
    });
  }));

  newFormCustomDocumentTypes = signal<FormCustomDocumentType[]>([]);

  formCustomDocumentTypes = computed(() => {
    const existingCustomDocumentTypes = this.model()?.filter(i => i.Id === null).map(i => new FormCustomDocumentType({Name: i.Name, Format: i.Format.Name, Status: i.Status, Transport: i.Transport, ConversationId: i.ConversationId})) ?? [];
    const newCustomDocumentTypes = this.newFormCustomDocumentTypes();
    return existingCustomDocumentTypes.concat(newCustomDocumentTypes) ?? [];
  });

  conversationToShow = computed(() => {
    if (!this.conversation()) return null;
    return new Conversation(
      {
        Id: this.conversation().Id, 
        AreaId: this.conversation().AreaId,
        UserId: 'partner',
        Comments: this.conversation().Comments?.map(cm => {
          const userId = cm.IsAction ? 'system' : (cm.FromCustomer ? 'customer' : 'partner');
          return new ConversationMessage({UserId: userId, Message: cm.Message, Timestamp: cm.Timestamp})
      }) ?? [], 
        LastRead: this.conversation().PartnerLastRead
      }
    );
  });


  // Form methods

  addCustomDocumentTypes() {
    this.newFormCustomDocumentTypes.update(i => [...i, new FormCustomDocumentType({Status: {Status: ItemStatus.Open, CreatedEntities: []}, ConversationId: null })]);
  }

  removeCustomDocumentTypes(idx: number) {
    const existingDocumentTypes = this.model()?.filter(i => i.Id !== null) ?? [];
    const existingCustomDocumentTypes = this.model()?.filter(i => i.Id === null) ?? [];

    if (idx < existingCustomDocumentTypes.length) {
      const remainingCustomDocumentTypes = existingCustomDocumentTypes.slice(0, idx).concat(existingCustomDocumentTypes.slice(idx + 1));
      this.model.set(existingDocumentTypes.concat(remainingCustomDocumentTypes));
    }
    else {      
      this.newFormCustomDocumentTypes.update(documentTypes => {
        const newDocumentTypesIdx = idx - existingCustomDocumentTypes.length;
        documentTypes.splice(newDocumentTypesIdx, 1);
        return [...documentTypes];
      });
    }
  }


  // Field state methods

  isDisabled(field: FormDocumentType) {
    const checkboxValue = this.form().form.get('selectedDocumentType-' + field.Id)?.getRawValue() as boolean ?? false;
    return !checkboxValue;
  }

  hasConversationId(field: FormDocumentType | FormCustomDocumentType) {
    return !!field.ConversationId;
  }

  isReadOnly(field: FormDocumentType | FormCustomDocumentType) {
    if (field.Status.Status === ItemStatus.Accepted || field.Status.Status === ItemStatus.AwaitingAcceptance) {
      return true;
    } 
    if (this.readonly()) {
      if (field.Status.Status === ItemStatus.Open) {
        return (field as FormDocumentType)?.Selected===false;
      }
      else {
        return true;
      }
    }
    return false;
  }


  isCustomFormat(fieldName: string) {
    const value = this.form().form.get(fieldName)?.getRawValue() as number ?? 0;
    return value===-1;
  }


  // Get selected document types from form value

  getSelectedDocumentTypes(formValue: unknown) {
    const documentTypes = this.formDocumentTypes().filter(i => {
      const selectedName = 'selectedDocumentType-' + i.Id;
      return formValue[selectedName] as boolean ?? false;
    }).map(i => {
      const valueName = 'documentTypeFormat-' + i.Id;
      const formatId = formValue[valueName] as number;
      let format: Format = null;
      if (formatId === -1) {
        const customDocumentTypesFormat = 'customDocumentTypesFormat-' + i.Id;
        const customFormat = formValue[customDocumentTypesFormat] as string;
        format = {Id: null, Name: customFormat};
      }
      else
      {
        format = i.PossibleFormats.find(f => f.Id === formatId);
      }

      const transportName = 'documentTypeTransport-' + i.Id;
      const transport = formValue[transportName] as Transport;

      return new SelectedDocumentType({Id: i.Id, Name: i.Name, Format: format, Status: i.Status, ConversationId: i.ConversationId, Transport: transport});
    });

    const customDocumentTypes = this.formCustomDocumentTypes().map((i, index) => {
      const typeName = 'customDocumentTypesName-' + index;
      const valueName = 'customDocumentTypesValue-' + index;
      const transportName = 'customDocumentTypesTransport-' + index;
      const type = formValue[typeName] as string;
      const value = formValue[valueName] as string;
      const transport = formValue[transportName] as Transport;
      return new SelectedDocumentType({Id: null, Name: type, Format: {Id: null, Name: value}, Status: i.Status, ConversationId: i.ConversationId, Transport: transport});
    });

    return documentTypes.concat(customDocumentTypes);
  }

  requestCredentials(transportType: TransportType) {
    this.requestCredentialsClicked.emit(transportType);
  }

  showCredentials(transportType: TransportType) {
    this.showCredentialsClicked.emit(transportType);
  }


  // Conversation methods

  hasUnreadMessages(conversationId: string): boolean {
    if (!this.unreadConversationIds()) {
      return false;
    }
    return this.unreadConversationIds()?.some(id => id === conversationId);
  }

  toggleConversation(conversationId: string, areaId: string|null) {
    const event = new ToggleConversationEvent({ConversationId: conversationId, AreaId: areaId});
    this.toggleConversationClicked.emit(event);
  }

  newComment(message: ConversationMessage, conversationId: string) {
    const comment = new PartnerOnboardingComment({FromCustomer: false, Message: message.Message, Timestamp: message.Timestamp});
    const newCommentEvent = new NewCommentEvent({Comment: comment, ConversationId: conversationId});
    this.newCommentClicked.emit(newCommentEvent);
  }

  newTransportComment(event: NewCommentEvent) {
    this.newCommentClicked.emit(event);
  }

  toggleAutoRefresh(): void {
    this.toggleAutoRefreshClicked.emit();
  }


  // ControlValueAccessor interface methods

  writeValue(obj: SelectedDocumentType[]): void {
    this.newFormCustomDocumentTypes.set([]);
    this.model.set(obj);
  }

  onChanged: (value: SelectedDocumentType[]) => void = () => { };
  registerOnChange(fn: (value: SelectedDocumentType[]) => void): void {
    this.onChanged = fn;
  }

  onTouched: () => void = () => { };
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    if (isDisabled) {
      this.form().form.disable();
    } 
    else {
      this.form().form.enable();
    }
  }


  // Validator interface methods

  validate() {
    return this.form().form.valid ? [] : ['Invalid'];
  }


  // OnDestroy interface methods

  ngOnDestroy(): void {
    if (this.formChangesSubscription) {
      this.formChangesSubscription.unsubscribe();
    }
  }

}
